/**
 * PGS Cloud Save Service (Phase 2)
 *
 * Saves/loads user progress to Google Play Games Saved Games.
 * Supabase is always authoritative — PGS cloud save is backup/restore only.
 */

import { Platform } from 'react-native';

import { supabase } from '../../lib/supabase';
import type { PGSCloudSavePayload } from '../types/playGames';

import * as playGamesService from './playGamesService';

const SAVE_SLOT = 'kbq_progress_v1';
const SAVE_DESCRIPTION = 'Kingdom Bible Quiz — Progress Save';
const AUTO_SAVE_DEBOUNCE_MS = 5 * 60 * 1000; // 5 minutes

let lastAutoSaveTime = 0;

/**
 * Save current user progress to PGS Saved Games.
 * Reads current state from Supabase, serializes, and writes.
 */
export async function saveProgress(userId: string): Promise<boolean> {
  if (Platform.OS !== 'android' || !playGamesService.isPGSAvailable()) return false;

  try {
    // Read current state from Supabase in parallel
    const [xpRes, statsRes, streakRes, achieveRes, spRes] = await Promise.all([
      supabase.from('user_xp').select('total_xp, current_level, rank').eq('user_id', userId).maybeSingle(),
      supabase.from('user_stats').select('current_streak, longest_streak').eq('user_id', userId).maybeSingle(),
      supabase.from('user_streaks').select('streak_days').eq('user_id', userId).maybeSingle(),
      supabase.from('user_achievements').select('achievement_key').eq('user_id', userId).eq('completed', true),
      supabase.from('singleplayer_results').select('category_id').eq('user_id', userId),
    ]);

    // Determine auth provider
    const { data: { user } } = await supabase.auth.getUser();
    const authProvider = user?.app_metadata?.provider || 'unknown';

    const payload: PGSCloudSavePayload = {
      version: 1,
      supabaseUserId: userId,
      authProvider,
      savedAt: new Date().toISOString(),
      totalXP: xpRes.data?.total_xp ?? 0,
      currentLevel: xpRes.data?.current_level ?? 1,
      rank: xpRes.data?.rank ?? 'Beginner',
      currentStreak: statsRes.data?.current_streak ?? streakRes.data?.streak_days ?? 0,
      longestStreak: statsRes.data?.longest_streak ?? 0,
      quizzesCompleted: (spRes.data ?? []).length,
      unlockedAchievements: (achieveRes.data ?? []).map(a => a.achievement_key),
      categoriesPlayed: [...new Set((spRes.data ?? []).map(r => r.category_id))],
      language: user?.user_metadata?.language || 'en',
      onboardingCompleted: true,
    };

    const serialized = JSON.stringify(payload);
    return await playGamesService.writeSavedGame(SAVE_SLOT, serialized, SAVE_DESCRIPTION);
  } catch (e) {
    console.warn('[PGS CloudSave] saveProgress error:', e);
    return false;
  }
}

/**
 * Load progress from PGS Saved Games.
 * Returns parsed payload or null if no save exists.
 */
export async function loadProgress(): Promise<PGSCloudSavePayload | null> {
  if (Platform.OS !== 'android' || !playGamesService.isPGSAvailable()) return null;

  try {
    const data = await playGamesService.readSavedGame(SAVE_SLOT);
    if (!data) return null;

    const payload = JSON.parse(data) as PGSCloudSavePayload;
    if (!payload.version || !payload.supabaseUserId) return null;

    return payload;
  } catch (e) {
    console.warn('[PGS CloudSave] loadProgress error:', e);
    return null;
  }
}

/**
 * Debounced auto-save — called after quiz completion.
 * Only saves once per 5 minutes to avoid excessive writes.
 */
export async function triggerAutoSave(userId: string): Promise<void> {
  if (Platform.OS !== 'android' || !playGamesService.isPGSAvailable()) return;

  const now = Date.now();
  if (now - lastAutoSaveTime < AUTO_SAVE_DEBOUNCE_MS) return;

  lastAutoSaveTime = now;

  // Fire-and-forget
  saveProgress(userId).catch(() => {});
}

/**
 * Delete PGS cloud save data.
 */
export async function deleteSave(): Promise<boolean> {
  if (Platform.OS !== 'android' || !playGamesService.isPGSAvailable()) return false;
  try {
    return await playGamesService.deleteSavedGame(SAVE_SLOT);
  } catch (e) {
    console.warn('[PGS CloudSave] deleteSave error:', e);
    return false;
  }
}
